import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { Check, ShieldCheck, Activity } from 'lucide-react';
import { Box, Flex, Text, SimpleGrid } from '@chakra-ui/react';
import Button from '../ui/Button';
import PaymentModal from '../ui/PaymentModal';

const MONTHLY_DISCOUNT = 0.15;
const ANNUAL_DISCOUNT = 0.3;

const formatUsd = (amount) => new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
  maximumFractionDigits: 2,
}).format(amount);

const getDiscountedMonthlyPrice = (monthlyPrice) => monthlyPrice * (1 - MONTHLY_DISCOUNT);

const getFullYearlyPrice = (monthlyPrice) => monthlyPrice * 12;

const getDiscountedYearlyPrice = (monthlyPrice) => monthlyPrice * 12 * (1 - ANNUAL_DISCOUNT);

const pricingTiers = [
  {
    name: 'Free',
    monthlyPrice: 0,
    description: 'Start syncing Jira and ClickUp at no cost.',
    features: ['15 min sync interval', '1 active integeration', '100 active sync items', 'Community support'],
    cta: 'Start Syncing for Free'
  },
  {
    name: 'Basic',
    monthlyPrice: 24,
    description: 'More integerations and faster sync for growing Jira and ClickUp teams.',
    features: ['5 min sync interval', '5 active integerations', '2,500 active sync items', 'Hierarchy-aware mapping', 'Priority support'],
    cta: 'Start With Basic',
    popular: true
  },
  {
    name: 'Pro',
    monthlyPrice: 69,
    description: 'For teams running Jira and ClickUp at scale.',
    features: ['Near real-time sync (<5 min)', 'Unlimited integerations', '25,000 active items in sync', 'Custom schema support', 'Dedicated account lead'],
    cta: 'Start With Pro'
  },
];

const Pricing = () => {
  const navigate = useNavigate();
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [user, setUser] = useState(null);

  React.useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => setUser(user));
    return unsubscribe;
  }, []);

  const handleGetStarted = (tier, index) => {
    if (index === 0 || tier.name === 'Free') {
      navigate('/signup');
      return;
    }
    if (!user) {
      navigate('/signup');
      return;
    }
    const planKey = tier.name.toLowerCase() === 'basic' ? 'bestseller' : 'pro';
    setSelectedPlan(planKey);
    setIsPaymentModalOpen(true);
  };

  return (
    <Box
      as="section"
      id="pricing"
      bg="landing.bg"
      py={{ base: 24, md: 32 }}
      fontFamily="body"
      position="relative"
      overflow="hidden"
    >
      {/* Background Decor */}
      <Box 
        position="absolute" 
        top="0" left="0" 
        w="100%" h="1px" 
        bg="linear-gradient(to right, transparent, brand.primary, transparent)" 
        opacity={0.3}
      />
      
      <Box maxW="1100px" mx="auto" px={{ base: 5, md: 8 }} position="relative" zIndex={1}>
        {/* Header */}
        <Box textAlign="center" mb={16}>
          <Flex
            display="inline-flex"
            align="center"
            gap={1.5}
            px={3} py={1.5}
            borderRadius="full"
            bg="brand.subtle"
            borderWidth="1px"
            borderColor="landing.border"
            mb={4}
            mx="auto"
            color="brand.primary"
          >
            <Activity size={14} />
            <Text fontSize="xs" fontWeight="700" color="brand.primary" letterSpacing="0.05em">
              PRICING
            </Text>
          </Flex>
          <Text
            as="h2"
            fontSize={{ base: '3xl', md: '5xl' }}
            fontWeight="900"
            color="text.main"
            letterSpacing="-0.03em"
            mb={4}
          >
            Pay for What You <Box as="span" color="brand.primary">Sync</Box>
          </Text>
          <Text fontSize="lg" color="text.muted" maxW="540px" mx="auto" lineHeight="1.7">
            No per-seat tax. No hidden fees. Pay for the Jira and ClickUp items DatLynk keeps in sync.
          </Text>
          <Text fontSize="sm" color="text.muted" maxW="620px" mx="auto" lineHeight="1.7" mt={3}>
            An active sync item is a Jira issue or ClickUp task that DatLynk monitors and keeps aligned.
          </Text>

          {/* Billing toggle */}
          <Flex
            display="inline-flex"
            align="center"
            bg="landing.surface"
            borderRadius="lg"
            borderWidth="1px"
            borderColor="landing.border"
            p={1}
            mt={6}
          >
            {['monthly', 'yearly'].map((cycle) => (
              <Box
                key={cycle}
                as="button"
                px={4} py={2}
                borderRadius="md"
                fontSize="sm"
                fontWeight="600"
                transition="all 150ms"
                border="none"
                cursor="pointer"
                style={{ fontFamily: 'inherit' }}
                bg={billingCycle === cycle ? 'brand.primary' : 'transparent'}
                color={billingCycle === cycle ? 'white' : 'text.secondary'}
                onClick={() => setBillingCycle(cycle)}
              >
                <Flex align="center" gap={2}>
                  {cycle === 'monthly' ? 'Monthly' : 'Yearly'}
                  <Box
                    px={1.5} py={0.5}
                    borderRadius="full"
                    bg="success.100"
                    color="success.800"
                    fontSize="10px"
                    fontWeight="700"
                  >
                    {cycle === 'monthly' ? '-15%' : '-30%'}
                  </Box>
                </Flex>
              </Box>
            ))}
          </Flex>
        </Box>

        {/* Pricing grid */}
        <SimpleGrid columns={{ base: 1, md: 3 }} gap={6} pt={{ base: 4, md: 0 }}>
          {pricingTiers.map((tier, index) => (
            (() => {
              const isYearly = billingCycle === 'yearly';
              const fullMonthlyPrice = tier.monthlyPrice;
              const fullYearlyPrice = getFullYearlyPrice(tier.monthlyPrice);
              const discountedPrice = isYearly
                ? getDiscountedYearlyPrice(tier.monthlyPrice)
                : getDiscountedMonthlyPrice(tier.monthlyPrice);
              const originalPrice = isYearly ? fullYearlyPrice : fullMonthlyPrice;
              const showDiscount = tier.monthlyPrice > 0;

              return (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              style={{ height: '100%' }}
            >
              <Box
                p={7}
                pt={tier.popular ? 10 : 7}
                borderRadius="2xl"
                bg={tier.popular ? 'rgba(37,99,235,0.04)' : 'landing.card'}
                borderWidth="2px"
                borderColor={tier.popular ? 'brand.primary' : 'landing.border'}
                boxShadow={tier.popular ? '0 0 0 1px rgba(37,99,235,0.12), 0 12px 40px rgba(37,99,235,0.10)' : 'shadows.card'}
                position="relative"
                overflow="visible"
                h="full"
                display="flex"
                flexDirection="column"
              >
                {tier.popular && (
                  <Box
                    position="absolute"
                    top="-14px"
                    left="50%"
                    transform="translateX(-50%)"
                    px={4} py={1.5}
                    borderRadius="full"
                    bg="brand.primary"
                    color="white"
                    fontSize="10px"
                    fontWeight="800"
                    letterSpacing="0.06em"
                    boxShadow="0 4px 12px rgba(37,99,235,0.35)"
                    whiteSpace="nowrap"
                  >
                    MOST POPULAR
                  </Box>
                )}

                <Box flex={1}>
                  <Box minH={{ base: 'auto', md: '88px' }}>
                    <Text
                      fontSize="xs"
                      fontWeight="700"
                      color="text.muted"
                      letterSpacing="0.08em"
                      textTransform="uppercase"
                      mb={1}
                    >
                      {tier.name}
                    </Text>
                    <Text
                      fontSize="sm"
                      color="text.secondary"
                      mb={5}
                      lineHeight="1.6"
                    >
                      {tier.description}
                    </Text>
                  </Box>

                  <Box minH={{ base: '60px', md: '72px' }} mb={1}>
                    <Text
                      fontSize="sm"
                      color="text.muted"
                      textDecoration="line-through"
                      visibility={showDiscount ? 'visible' : 'hidden'}
                      mb={showDiscount ? 0.5 : 0}
                    >
                      {formatUsd(originalPrice)}
                    </Text>
                    <Flex align="baseline" gap={1}>
                      <Text
                        fontSize="4xl"
                        fontWeight="800"
                        color="text.main"
                        letterSpacing="-0.03em"
                      >
                        {formatUsd(discountedPrice)}
                      </Text>
                      <Text fontSize="sm" color="text.muted">
                        {isYearly ? '/yr' : '/mo'}
                      </Text>
                    </Flex>
                  </Box>
                  <Flex direction="column" gap={3} mb={8}>
                    {tier.features.map((feature, i) => (
                      <Flex key={i} align="center" gap={3}>
                        <Flex
                          w="18px" h="18px"
                          borderRadius="full"
                          bg={tier.popular ? 'brand.subtle' : 'success.50'}
                          align="center"
                          justify="center"
                          flexShrink={0}
                          color={tier.popular ? 'brand.primary' : 'success.600'}
                        >
                          <Check size={11} strokeWidth={3} />
                        </Flex>
                        <Text fontSize="sm" color="text.secondary">
                          {feature}
                        </Text>
                      </Flex>
                    ))}
                  </Flex>
                </Box>

                <Box mt="auto">
                  <Button
                    variant={tier.popular ? "cta" : "secondary"}
                    size="large"
                    onClick={() => handleGetStarted(tier, index)}
                    width="full"
                  >
                    {tier.cta}
                  </Button>
                  <Flex align="center" justify="center" gap={1.5} mt={3} color="text.muted">
                    <ShieldCheck size={12} />
                    <Text fontSize="11px">Secure checkout</Text>
                  </Flex>
                </Box>
              </Box>
            </motion.div>
              );
            })()
          ))}
        </SimpleGrid>
      </Box>

      <PaymentModal
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
        selectedPlan={selectedPlan}
        showAllPlans={false}
      />
    </Box>
  );
};

export default Pricing;
